import Store, { actions } from '@store';

import { logError } from 'src/utils/logger';
const CompanyFactory = require('./company').instance;

const DesignFacade = require('../facade/design/design');
const TextRestrictions = require('../facade/design/setters/restrictions/text');
const ProjectFacade = require('../facade/project/project');
const RestrictionsFacade = require('../facade/restrictions/restrictions');

const DefaultTextValues = require('./restrictions/default-values/text');
const defaults = require('./restrictions/defaults.json');
const UserInfoFactory = require('./user-info');
let templateData;

function RestrictionsFactory () {
  _.bindAll(this,
    'initialize',
    'initListeners',
    'getElementPermissions',
    'getTemplatePermissions',
    'getTemplatePermissionsType',
    'initSimplifiedPermissions'
  );

  this.unsavedElementChanges = [];
  this.unsavedTemplateChanges = [];
  this.elementOriginalContent = {};
  this.saveTimer = null;
  this.correlatedPermissions = {
    vector_add: ['function_draw'],
    function_change_layer_order: [
      'logo_z_index', 'vector_z_index',
      'image_z_index', 'qrcode_z_index', 'text_z_index',
      'icon_z_index', 'table_z_index', 'logo_z_index'
    ]
  };

  this.deprecatedBasicElementPermissions = {
    function_change_layer_order: [
      'logo_z_index', 'vector_z_index',
      'image_z_index', 'qrcode_z_index', 'text_z_index',
      'icon_z_index', 'table_z_index', 'logo_z_index'
    ]
  };

  this.listeners = {
    logo: require('./restrictions/listeners/logo'),
    icon: require('./restrictions/listeners/logo'),
    vector: require('./restrictions/listeners/logo'),
    image: require('./restrictions/listeners/image'),
    video: require('./restrictions/listeners/video'),
    background: require('./restrictions/listeners/background')
  };
}

RestrictionsFactory.prototype = _.extend({}, Backbone.Events);

RestrictionsFactory.prototype.initialize = _.memoize(function () {
  const deferred = $.Deferred();

  if (INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE) {
    ProjectFacade.on('design.added', _.bind(onPageAdded, this));
    ProjectFacade.on('design.copied', _.bind(onPageAdded, this));

    ProjectFacade.on('design.removed', _.bind(function (data) {
      removePageElementPermissions.call(this, data.page_number);

      this.getElementPermissionsCollection().each(function (p) {
        if (p.get('page_number') > data.page_number) {
          p.set('page_number', p.get('page_number') - 1);
          this.unsavedElementChanges.push(p);
        }
      }, this);
    }, this));

    DesignFacade.on('template.update', (data) => {
      if (data?.skipRemoveRestrictions) return;
      removePageElementPermissions.call(this, INKIVE.Tool._current_page_number);
    });
  }

  loadCredentials()
    .then(getTemplate)
    .then(getPermissions.bind(this))
    .then(this.initListeners)
    .then(() => {
      this.initSimplifiedPermissions().then(deferred.resolve);
    });

  return deferred.promise();

  function onPageAdded (data) {
    this.getElementPermissionsCollection().each(function (p) {
      if (p.get('page_number') >= data.design_number) {
        p.set('page_number', p.get('page_number') + 1);
        this.unsavedElementChanges.push(p);
      }
    }, this);
  }

  function removePageElementPermissions (page) {
    this.getElementPermissionsCollection().each(function (p) {
      if (p.get('page_number') === page) {
        this.unsavedElementChanges = _.without(this.unsavedElementChanges, p);
        _.defer(function () { p.destroy(); });
      }
    }, this);
  }

  function loadCredentials () {
    const deferred = $.Deferred();

    UserInfoFactory.get().then(function (data) {
      CompanyFactory.setCredentials({
        username: data.credentials.token,
        password: data.credentials.hash
      });

      deferred.resolve();
    });

    return deferred.promise();
  }

  function getTemplate () {
    if (INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE) {
      return CompanyFactory.get(['templates', INKIVE.C.BOOK.encodedId]);
    }

    return CompanyFactory.get(['designs', INKIVE.C.BOOK.encodedId])
      .then(({ template }) => {
        if (!template) return {};

        return CompanyFactory.get(['templates', template]);
      });
  }

  function getPermissions (data) {
    templateData = data;

    if (templateData.id) {
      return $.when(
        CompanyFactory.get(['templates', templateData.id, 'templatepermissions']),
        CompanyFactory.get(['templates', templateData.id, 'elementpermissions'])
      ).then((templatePermissions, elementPermissions) => {
        const safeElementPermissions = this.filterBrokenPermissions(elementPermissions);

        Store.dispatch(actions.permission.setTemplatePermission(templatePermissions?.[0]));
        Store.dispatch(actions.permission.setElementPermission(safeElementPermissions));

        return $.Deferred().resolve(templatePermissions, safeElementPermissions);
      });
    }

    return $.Deferred().resolve();
  }
});

RestrictionsFactory.prototype.initListeners = function () {
  const deferred = $.Deferred();

  _.each(this.listeners, function (listener) {
    listener.initialize(this);
  }, this);

  // templateData.id is defined if the design has a base template (not started from blank)
  templateData.id && refreshListeners.call(this);
  deferred.resolve.apply(deferred, arguments);

  return deferred.promise();

  function refreshListeners () {
    this.getElementPermissionsCollection()
      .chain()
      .filter(isFromSamePage)
      .each(this.refreshListener, this);
  }

  function isFromSamePage (p) {
    return p.get('page_number') === INKIVE.Tool._current_page_number;
  }
};

RestrictionsFactory.prototype.refreshListener = function (permissions) {
  const id = permissions.get('element_id');
  const type = id === 'background'
    ? 'background'
    : RestrictionsFacade.getElementType($('#' + id)[0]);

  this.listeners[type] && this.listeners[type].refresh(permissions);
};

RestrictionsFactory.prototype.getTemplatePermissionsCollection = function () {
  return CompanyFactory.getContainer('templates', templateData.id, 'templatepermissions');
};

RestrictionsFactory.prototype.getElementPermissionsCollection = function () {
  return this.filterBrokenElementPermissionFromCollection(
    CompanyFactory.getContainer('templates', templateData.id, 'elementpermissions')
  );
};

RestrictionsFactory.prototype.getTemplatePermissions = function () {
  return this.initialize().then(function () {
    return CompanyFactory.get(['templates', templateData.id, 'templatepermissions']);
  });
};

RestrictionsFactory.prototype.getTemplatePermissionsType = function (permissions) {
  const types = ['loose', 'tight', 'simplified'];
  const {
    id,
    sticky,
    ...rest
  } = permissions;
  delete rest.static; // not possible to destructure, reserved word

  for (const type of types) {
    const {
      background,
      ...defaults
    } = this.getDefaultsByType(type);

    if (INKIVE.Helpers.isShallowEqual(rest, defaults)) return type;
  }

  return types[0];
};

RestrictionsFactory.prototype.getElementPermissions = function () {
  return this.initialize().then(function () {
    return CompanyFactory.get(['templates', templateData.id, 'elementpermissions']);
  });
};

RestrictionsFactory.prototype.createGlobalTemplateRestriction = function (type) {
  const defaults = this.getDefaultsByType(type);
  const deferred = $.Deferred();

  const permissions = this.getTemplatePermissionsCollection()
    .find(function (p) { return !p.get('group'); });

  permissions && permissions.destroy();

  const models = [
    this.getTemplatePermissionsCollection().create(_.extend(
      _.omit(defaults, 'background'),
      { template: templateData.id }
    )),

    ...Array.from({ length: INKIVE.C.BOOK.num_pages }, (element, index) =>
      this.getElementPermissionsCollection()
        .create({
          page_number: index + 1, // Index + 1 = Page number
          element_id: 'background',
          template: templateData.id,
          restrictions: JSON.stringify(defaults.background)
        })
    )
  ];

  let onDone = _.bind(deferred.resolve, this, models[0]);
  onDone = _.after(models.length, onDone);

  _.each(models, function (model) { model.on('change:id', onDone); });

  return deferred.promise();
};

RestrictionsFactory.prototype.setGlobalTemplateRestriction = function (permissionName, permissionValue) {
  const deferred = $.Deferred();

  /**
   *
   * @param permission
   * @param {string[]} keys
   * @return {boolean}
   */
  const removePermissions = (permission, keys) => {
    let removedPermission = false;
    for (const key in permission) {
      if (keys.includes(key)) {
        permission.unset(key);
        removedPermission = true;
      }
    }

    return removedPermission;
  };

  getTemplatePermissions.call(this)
    .then((permissions) => {
      if (this.deprecatedBasicElementPermissions[permissionName]) {
        const deprecatedBasicPermissions = this.deprecatedBasicElementPermissions[permissionName];
        const allElementPermissions = this.getElementPermissionsCollection();
        allElementPermissions.forEach(elementPermission => {
          const restrictions = (JSON.parse(elementPermission.get('restrictions')));
          if (removePermissions(restrictions.basic, deprecatedBasicPermissions)) {
            elementPermission.set('restrictions', JSON.stringify(restrictions));
            this.unsavedElementChanges.push(elementPermission);
          }
        });
      }

      if (this.correlatedPermissions[permissionName]) {
        this.correlatedPermissions[permissionName].forEach((correlatedPermission) => {
          permissions.set(correlatedPermission, permissionValue);
        });
      }

      permissions.set(permissionName, permissionValue);

      this.storeTemplateChange(permissions);

      return deferred.resolve();
    });

  return deferred.promise();

  function getTemplatePermissions () {
    const permissions = this.getTemplatePermissionsCollection()
      .find(function (p) { return !p.get('group'); });

    if (permissions) {
      const deferred = $.Deferred();
      deferred.resolve(permissions);

      return deferred.promise();
    }

    return this.createGlobalTemplateRestriction();
  }
};

RestrictionsFactory.prototype.getGlobalRestrictionElementPermissions = function (elementId, restrictions, name = undefined, pageNumber = INKIVE.Tool._current_page_number) {
  const deferred = $.Deferred();
  this.initialize().then(() => {
    const permissions = this.getElementPermissionsCollection()
      .find(function (p) {
        return !p.get('group') &&
          p.get('page_number') === pageNumber &&
          p.get('element_id') === elementId;
      });

    if (permissions) {
      deferred.resolve(permissions);
    } else {
      restrictions
        ? createNewPermission.call(this, deferred)
        : deferred.resolve(null);
    }
  });

  return deferred.promise();

  function createNewPermission (deferred) {
    const model = this.getElementPermissionsCollection()
      .create({
        page_number: pageNumber,
        element_id: elementId,
        name,
        template: templateData.id,
        restrictions
      });

    model.on('change:id', _.bind(deferred.resolve, this, model));
  }
};

RestrictionsFactory.prototype.setGlobalRestrictionName = function (elementId, name) {
  const deferred = $.Deferred();

  this.getGlobalRestrictionElementPermissions(elementId).then(_.bind(function (permissions) {
    if (permissions) {
      permissions.set('name', name);
      this.storeElementChange(permissions);
    }

    deferred.resolve();
  }, this));

  return deferred.promise();
};

RestrictionsFactory.prototype.setGlobalElementRestriction = function (elementId, restrictions, name = undefined, pageNumber = INKIVE.Tool._current_page_number) {
  const deferred = $.Deferred();
  this.getGlobalRestrictionElementPermissions(elementId, restrictions, name, pageNumber).then(_.bind(function (permissions) {
    if (permissions) {
      permissions.set('restrictions', restrictions);
      this.storeElementChange(permissions, pageNumber);
      this.setElementContent(elementId, restrictions);
    }

    deferred.resolve();
  }, this));

  return deferred.promise();
};

RestrictionsFactory.prototype.setElementContent = function (elementId, restrictions) {
  const element = DesignFacade.get(elementId);

  if (restrictions && element && element.type === 'text') {
    const initialContent = element.content;
    const restrictionObject = JSON.parse(restrictions);

    if (TextRestrictions.contentCompliesWithRestrictions(initialContent, restrictionObject)) {
      return;
    }

    if (!this.elementOriginalContent[elementId]) {
      this.elementOriginalContent[elementId] = initialContent;
    }

    let newContent = DefaultTextValues.getDefaultRestrictionValue(restrictionObject);

    if (!newContent) {
      newContent = this.elementOriginalContent[elementId];
      delete this.elementOriginalContent[elementId];
    }

    newContent &&
    newContent !== initialContent &&
    INKIVE.DesignFacade.set(elementId, { content: newContent.toString() });
  }
};

RestrictionsFactory.prototype.storeElementChange = function (permissions, pageNumber = INKIVE.Tool._current_page_number) {
  const groupId = permissions.get('group');
  const elementId = permissions.get('element_id');
  const hasAnyRestriction = permissions.get('restrictions') &&
    _.size(JSON.parse(permissions.get('restrictions')));

  removeDuplicates.call(this, pageNumber);
  this.unsavedElementChanges = _.without(this.unsavedElementChanges, permissions);

  this.refreshListener(permissions);
  hasAnyRestriction && this.unsavedElementChanges.push(permissions);
  hasAnyRestriction || permissions.destroy();

  function removeDuplicates (pageNumber = INKIVE.Tool._current_page_number) {
    this.getElementPermissionsCollection().each(function (p) {
      const isSame = p !== permissions &&
        p.get('page_number') === pageNumber &&
        p.get('element_id') === elementId &&
        p.get('group') === groupId;

      if (isSame) {
        this.unsavedElementChanges = _.without(this.unsavedElementChanges, p);
        _.defer(_.bind(function () {
          p.set('restrictions', null);
          this.refreshListener(p);
          p.destroy();
        }, this));
      }
    }, this);
  }
};

RestrictionsFactory.prototype.storeTemplateChange = function (permissions) {
  const groupId = permissions.get('group');

  removeDuplicates.call(this);
  this.unsavedTemplateChanges = _.without(this.unsavedTemplateChanges, permissions);
  this.unsavedTemplateChanges.push(permissions);

  function removeDuplicates () {
    this.getTemplatePermissionsCollection().each(function (p) {
      const isSame = p.get('group') === groupId && p !== permissions;

      if (isSame) {
        this.unsavedTemplateChanges = _.without(this.unsavedTemplateChanges, p);
        _.defer(function () { p.destroy(); });
      }
    });
  }
};

RestrictionsFactory.prototype.isAllSaved = function (permissions) {
  return !this.unsavedElementChanges.length &&
    !this.unsavedTemplateChanges.length;
};

RestrictionsFactory.prototype.save = function (debounce) {
  if (!debounce) {
    this._save(debounce);
  } else {
    const debounceTime = 3000;

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    this.saveTimer = setTimeout(() => {
      this._save(debounce);
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }, debounceTime);
  }
};

RestrictionsFactory.prototype.ongoingSaves = [];

RestrictionsFactory.prototype._save = function (debounce) {
  // Abort ongoing save operations
  this.ongoingSaves.forEach(function (saveOperation) {
    if (saveOperation && typeof saveOperation.abort === 'function' && debounce) {
      saveOperation.abort();
    }
  });

  // Clear the list of ongoing saves
  this.ongoingSaves = [];

  const hasUnsavedChanges = this.unsavedElementChanges.length ||
    this.unsavedTemplateChanges.length;

  if (!hasUnsavedChanges) {
    const deferred = $.Deferred();
    deferred.resolve();
    return deferred.promise();
  }

  const promises = [];
  while (this.unsavedElementChanges.length) {
    const permissions = this.unsavedElementChanges.pop();
    if (permissions.get('element_id')) {
      const saveOperation = permissions.save();
      this.ongoingSaves.push(saveOperation); // Track the save operation
      promises.push(saveOperation);
    }
  }

  while (this.unsavedTemplateChanges.length) {
    const unsaved = this.unsavedTemplateChanges.pop();
    if (unsaved) {
      const saveOperation = unsaved.save();
      this.ongoingSaves.push(saveOperation); // Track the save operation
      promises.push(saveOperation);
    }
  }

  return $.when.apply($, promises).then(() => {
    // Clear the list of ongoing saves once all saves are completed
    this.ongoingSaves = [];
  });
};

RestrictionsFactory.prototype.getDefaultsByType = function (type) {
  switch (type) {
    case 'simplified':
      return defaults.simplified;
    case 'tight':
      return defaults.tight;
    case 'moderate':
    // falls through to loose
    case 'loose':
    default:
      return defaults.loose;
  }
};

RestrictionsFactory.prototype.removeAll = function () {
  this.getTemplatePermissionsCollection().each(deferredDestroy);
  this.getElementPermissionsCollection().each(deferredDestroy);

  this.unsavedElementChanges = [];

  function deferredDestroy (p) {
    _.defer(function () {
      p.destroy();
    });
  }
};

RestrictionsFactory.prototype.copyRestrictions = function (page) {
  const setGlobalElementRestriction = _.bind(this.setGlobalElementRestriction, this);
  return this.getElementPermissions().then(_copyElementPermissions);

  function _copyElementPermissions (elementPermissions) {
    if (_.size(elementPermissions)) {
      const p = elementPermissions.pop();

      return p.page_number === page
        ? setGlobalElementRestriction(p.element_id, p.restrictions, p.name, INKIVE.Tool._current_page_number + 1)
          .then(_.partial(_copyElementPermissions, elementPermissions))
        : _copyElementPermissions(elementPermissions);
    }

    const deferred = $.Deferred();
    deferred.resolve();
    return deferred.promise();
  }
};

RestrictionsFactory.prototype.initSimplifiedPermissions = function () {
  return new Promise(resolve => {
    if (INKIVE.C.BOOK.book_type !== INKIVE.C.BOOK_TYPE.TEMPLATE) {
      resolve();
    } else {
      CompanyFactory.getCompanyData().then((companyData) => {
        const isTemplateGranularPermission = companyData.features.template_restrictions?.includes?.('enable_template_granular_permission');
        const defaultPermission = isTemplateGranularPermission ? 'loose' : 'simplified';
        INKIVE.Menus.showSimplifiedRestrictions = !isTemplateGranularPermission;
        this.getTemplatePermissions().then((permissions) => {
          permissions.length || this.createGlobalTemplateRestriction(defaultPermission);
        });

        resolve();
      });
    }
  });
};

RestrictionsFactory.prototype.filterBrokenElementPermissionFromCollection = function (collection) {
  collection.reset(collection.models.filter(item => {
    try {
      /**
       * Clip elements are not meant to have restrictions defined
       * These elements are part of other elements and should not have restrictions
      */
      const elementId = item.attributes.element_id;
      if (!elementId.startsWith('svg_') && !elementId.includes('background')) return false;

      JSON.parse(item.attributes.restrictions);
      return true;
    } catch (error) {
      console.warn('Broken element permission detected:', item.attributes);
      logError('FilterBrokenPermissions', item.attributes)(error);
      return false;
    }
  }));

  return collection;
};

RestrictionsFactory.prototype.filterBrokenPermissions = function (permissions) {
  return permissions.filter(item => {
    try {
      JSON.parse(item.restrictions);
      return true;
    } catch (error) {
      console.warn('Broken element permission detected:', item);
      return false;
    }
  });
};

const RestrictionsFactoryInstance = new RestrictionsFactory();
module.exports = RestrictionsFactoryInstance;
